import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import {
  Check,
  Crown,
  Zap,
  Star,
  ArrowRight,
  Sparkles,
  Coins,
  ChevronDown,
  Shield,
  Gift,
  Building,
  MessageSquare,
  Search,
  Infinity,
} from 'lucide-react';
import { LoadingSpinner } from '@/components/ui';
import { SpotlightCard, SectionLabel, VerdictFooter } from '@/components/verdict';
import { PublicNavbar } from '@/components/layout';
import { useTiers } from '@/hooks/useTiers';
import { cn } from '@/lib/utils';
import type { Tier } from '@/types';

// ============================================================================
// Credit Pack Data (matches backend credit pack definitions)
// ============================================================================
const CREDIT_PACKS = [
  {
    id: 'starter',
    name: 'Starter Pack',
    credits: 600,
    price: 5,
    bonusPercent: 20,
    description: 'Perfect for trying out premium features',
    icon: Zap,
  },
  {
    id: 'researcher',
    name: 'Researcher Pack',
    credits: 2600,
    price: 20,
    bonusPercent: 30,
    description: 'Best value for regular research',
    icon: Star,
    isBestValue: true,
  },
  {
    id: 'power',
    name: 'Power Pack',
    credits: 7000,
    price: 50,
    bonusPercent: 40,
    description: 'Maximum credits for power users',
    icon: Crown,
  },
];

// ============================================================================
// Subscription Tier Display Config
// ============================================================================
const tierIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  trial: Gift, analyst: Zap, partner: Star, team: Crown,
  basic: Zap, standard: Star, professional: Crown,
  master: Shield, enterprise: Building,
};

const tierLabels: Record<string, string> = {
  analyst: 'Analyst', partner: 'Partner', team: 'Team',
  basic: 'Analyst', standard: 'Partner', professional: 'Team',
};

const TIER_CREDIT_INFO: Record<string, { credits: string; rolloverCap: string; isUnlimited?: boolean }> = {
  analyst: { credits: '1,350', rolloverCap: '2,700' },
  partner: { credits: '4,600', rolloverCap: '9,200' },
  team: { credits: 'Unlimited', rolloverCap: 'N/A', isUnlimited: true },
  basic: { credits: '1,350', rolloverCap: '2,700' },
  standard: { credits: '4,600', rolloverCap: '9,200' },
  professional: { credits: 'Unlimited', rolloverCap: 'N/A', isUnlimited: true },
};

// ============================================================================
// Credit-to-Query Estimates (derived from backend agent costs)
// Financial: ~40-60 credits, Strategic: ~100-130 credits, General: ~80-120 credits
// ============================================================================
function estimateQueries(credits: number) {
  return {
    financial: `~${Math.floor(credits / 60)}-${Math.floor(credits / 40)}`,
    strategic: `~${Math.floor(credits / 130)}-${Math.floor(credits / 100)}`,
  };
}

// ============================================================================
// FAQ Data
// ============================================================================
const FAQ_ITEMS = [
  { q: 'What are credits?', a: 'Credits are the currency for deep research on Vettis. Each research query costs credits based on complexity. Follow-up questions and chat messages are unlimited and never consume credits.' },
  { q: 'Do top-up credits expire?', a: 'No. Top-up credits carry over indefinitely until used. Subscription credits roll over up to your plan\'s cap and reset each billing period.' },
  { q: 'Can I use top-ups without a subscription?', a: 'Absolutely. Credit packs are standalone with no commitment. Buy once, use anytime. You get access to ALL research modes and AI models.' },
  { q: 'What happens when I run out of credits?', a: 'You can still chat and ask follow-up questions. To start new deep research, purchase more credits or wait for your next billing period refill.' },
  { q: 'Can I cancel anytime?', a: 'Yes. Cancel your subscription anytime from your account settings. Your remaining credits stay active until the end of your billing period.' },
  { q: 'What AI models do I get access to?', a: 'All plans give you access to every AI model: GPT-4o, Claude, Gemini, DeepSeek, and more. No model restrictions on any plan.' },
  { q: 'Can I analyze my investment portfolio?', a: 'Yes. Portfolio analysis is available in early access for all paid plans. Upload your Fidelity CSV or PDF, and Vettis will run an adversarial bull/bear scan of every position, flag holdings that need attention, and let you deep dive into any stock.' },
  { q: 'What is the Teams plan unlimited credits?', a: 'The Teams plan provides truly unlimited credits with no monthly cap or rollover limits. Run as many deep research queries, financial analyses, and portfolio scans as you need. Ideal for teams and power users who need unrestricted access.' },
];

// ============================================================================
// Sub-Components
// ============================================================================
function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-border-subtle last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-5 text-left group"
      >
        <span className="text-sm sm:text-base font-medium text-text-primary group-hover:text-verdict-primary transition-colors pr-4">
          {question}
        </span>
        <ChevronDown className={cn('w-5 h-5 text-text-secondary transition-transform duration-300 flex-shrink-0', isOpen && 'rotate-180 text-verdict-primary')} />
      </button>
      <div className={cn('overflow-hidden transition-all duration-300', isOpen ? 'max-h-96 pb-5' : 'max-h-0')}>
        <p className="text-sm text-text-secondary leading-relaxed">{answer}</p>
      </div>
    </div>
  );
}

function formatTierPrice(tier: Tier, period: 'monthly' | 'annual') {
  if (!tier.pricing) return { price: '$0', period: '', originalPrice: null, annualTotal: null };
  if (period === 'annual') {
    const annualPrice = tier.pricing.monthly * 10;
    const monthlyEquivalent = annualPrice / 12;
    return { price: `$${monthlyEquivalent.toFixed(2)}`, period: '/mo', originalPrice: `$${tier.pricing.monthly.toFixed(2)}`, annualTotal: `$${annualPrice.toFixed(0)}/yr` };
  }
  return { price: `$${tier.pricing.monthly.toFixed(2)}`, period: '/mo', originalPrice: null, annualTotal: null };
}

export function PricingPage() {
  const { tiers, isLoading } = useTiers();
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');

  return (
    <div className="relative min-h-screen bg-background text-text-primary overflow-x-hidden">
      <SEO
        title="Pricing - Vettis"
        description="Flexible AI research pricing. Buy credits as you go — from starter packs to researcher plans with bonus credit rewards. Unlimited chat included."
        path="/pricing"
      />
      {/* Ambient Effects */}
      <div className="verdict-grain" />
      <div className="verdict-vignette" />
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-verdict-primary/5 rounded-full blur-[120px] pointer-events-none" />

      {/* Navigation */}
      <PublicNavbar currentPage="/pricing" />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 sm:pt-40 sm:pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <SectionLabel centered className="mb-6">PRICING</SectionLabel>
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-text-primary via-verdict-primary to-text-primary">
              Power Your Decisions
            </span>
          </h1>
          <p className="text-text-secondary text-sm sm:text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
            Credits fuel every deep research query, financial deep dive, and portfolio scan.
            <br className="hidden sm:block" />
            Unlimited chat & follow-ups included with every plan.
          </p>
          <div className="flex flex-wrap justify-center gap-4 sm:gap-8 text-xs sm:text-sm text-text-muted">
            <span className="flex items-center gap-1.5">
              <Check className="w-4 h-4 text-verdict-primary" />
              All AI models included
            </span>
            <span className="flex items-center gap-1.5">
              <Check className="w-4 h-4 text-verdict-primary" />
              Unlimited chat & follow-ups
            </span>
            <span className="flex items-center gap-1.5">
              <Check className="w-4 h-4 text-verdict-primary" />
              Cancel subscriptions anytime
            </span>
          </div>
        </div>
      </section>

      {/* ===== TOP-UP CREDIT PACKS ===== */}
      <section className="relative py-16 sm:py-24 border-t border-border-subtle">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <SectionLabel centered className="mb-4">TOP-UP PACKS</SectionLabel>
            <h2 className="text-2xl sm:text-4xl font-bold tracking-tight mb-4">
              Need More Credits? <span className="text-verdict-primary">No Commitment.</span>
            </h2>
            <p className="text-text-secondary text-sm sm:text-base max-w-xl mx-auto">
              Buy once, use anytime. Top-up credits never expire. The more you buy, the bigger the bonus.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            {CREDIT_PACKS.map((pack) => {
              const PackIcon = pack.icon;
              return (
                <SpotlightCard
                  key={pack.id}
                  className={cn(pack.isBestValue && 'ring-1 ring-verdict-primary/40')}
                  contentClassName="p-6 sm:p-8"
                >
                  {pack.isBestValue && (
                    <div className="flex justify-center mb-4">
                      <span className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider bg-verdict-primary text-black rounded-full">
                        Best Value
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-verdict-primary/10 flex items-center justify-center">
                      <PackIcon className="w-5 h-5 text-verdict-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{pack.name}</h3>
                      <p className="text-xs text-text-muted">{pack.description}</p>
                    </div>
                  </div>
                  <div className="mb-4">
                    <span className="text-3xl sm:text-4xl font-bold">${pack.price}</span>
                    <span className="text-text-muted ml-1 text-sm">one-time</span>
                  </div>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-text-secondary">Credits</span>
                      <span className="font-semibold text-text-primary">{pack.credits.toLocaleString()}</span>
                    </div>
                    {pack.bonusPercent > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-text-secondary">Bonus</span>
                        <span className="font-semibold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                          <Sparkles className="w-3 h-3" />
                          +{pack.bonusPercent}% vs pay-as-you-go
                        </span>
                      </div>
                    )}
                    <div className="h-px bg-white/10" />
                    {/* Credit-to-query translation */}
                    <div className="text-[11px] text-text-muted space-y-1 py-1">
                      <div className="flex justify-between">
                        <span>Financial analyses</span>
                        <span className="text-text-primary">{estimateQueries(pack.credits).financial}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Strategic blueprints</span>
                        <span className="text-text-primary">{estimateQueries(pack.credits).strategic}</span>
                      </div>
                    </div>
                    <div className="h-px bg-white/10" />
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-text-muted">Includes</span>
                      <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                        Unlimited chat & follow-ups
                      </span>
                    </div>
                  </div>
                  <Link to="/signup" className="block">
                    <button className={cn(
                      'w-full py-3 rounded-lg text-sm font-medium transition-all duration-200',
                      pack.isBestValue
                        ? 'bg-verdict-primary text-black hover:bg-verdict-primary/90'
                        : 'border border-border-subtle text-text-primary hover:bg-surface-light'
                    )}>
                      Get {pack.credits.toLocaleString()} Credits
                      <ArrowRight className="w-4 h-4 inline ml-2" />
                    </button>
                  </Link>
                </SpotlightCard>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== SUBSCRIBE & SAVE ===== */}
      <section className="relative py-16 sm:py-24 border-t border-border-subtle">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <SectionLabel centered className="mb-4">SUBSCRIBE & SAVE</SectionLabel>
            <h2 className="text-2xl sm:text-4xl font-bold tracking-tight mb-4">
              Monthly Credits. <span className="text-verdict-primary">Best Value.</span>
            </h2>
            <p className="text-text-secondary text-sm sm:text-base max-w-xl mx-auto mb-6">
              Monthly credit allocations with automatic refills and rollover. Plus unlimited chat & follow-ups.
            </p>
            <div className="inline-flex items-center rounded-full border border-border-subtle bg-surface-light p-1">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={cn(
                  'px-4 py-1.5 rounded-full text-sm transition-all duration-200',
                  billingPeriod === 'monthly' ? 'bg-verdict-primary text-black font-medium' : 'text-text-secondary hover:text-text-primary'
                )}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingPeriod('annual')}
                className={cn(
                  'px-4 py-1.5 rounded-full text-sm transition-all duration-200',
                  billingPeriod === 'annual' ? 'bg-verdict-primary text-black font-medium' : 'text-text-secondary hover:text-text-primary'
                )}
              >
                Annual
                <span className="ml-1 text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">2 months free</span>
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-16"><LoadingSpinner size="lg" /></div>
          ) : (
            <div className="grid sm:grid-cols-3 gap-6">
              {tiers.map((tier) => {
                const Icon = tierIcons[tier.tier] || Star;
                const label = tierLabels[tier.tier] || tier.tier;
                const creditInfo = TIER_CREDIT_INFO[tier.tier];
                const isPopular = tier.tier === 'partner' || tier.tier === 'standard';
                const isTeam = tier.tier === 'team' || tier.tier === 'professional';
                const priceInfo = formatTierPrice(tier, billingPeriod);
                return (
                  <SpotlightCard
                    key={tier.tier}
                    className={cn(
                      isPopular && 'ring-1 ring-verdict-primary/40',
                      isTeam && 'ring-1 ring-amber-500/40'
                    )}
                    contentClassName="p-6 sm:p-8"
                  >
                    {isPopular && (
                      <div className="flex justify-center mb-4">
                        <span className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider bg-verdict-primary text-black rounded-full flex items-center gap-1">
                          <Sparkles className="w-3 h-3" /> Most Popular
                        </span>
                      </div>
                    )}
                    {isTeam && (
                      <div className="flex justify-center mb-4">
                        <span className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-amber-500 to-orange-500 text-black rounded-full flex items-center gap-1">
                          <Infinity className="w-3 h-3" /> Unlimited
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-10 h-10 rounded-lg bg-verdict-primary/10 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-verdict-primary" />
                      </div>
                      <h3 className="font-semibold text-lg capitalize">{label}</h3>
                    </div>
                    <div className="mb-1">
                      {priceInfo.originalPrice && (
                        <span className="text-sm text-text-muted line-through mr-2">{priceInfo.originalPrice}</span>
                      )}
                      <span className="text-3xl sm:text-4xl font-bold">{priceInfo.price}</span>
                      <span className="text-text-muted text-sm">{priceInfo.period}</span>
                    </div>
                    {priceInfo.annualTotal ? (
                      <p className="text-xs text-emerald-600 dark:text-emerald-400 mb-4">{priceInfo.annualTotal} billed annually</p>
                    ) : <div className="mb-4" />}
                    <p className="text-xs text-text-muted mb-5">{tier.tagline}</p>
                    {creditInfo && (
                      <div className="space-y-3 mb-6">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-text-secondary">Monthly Credits</span>
                          {creditInfo.isUnlimited ? (
                            <span className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400">Unlimited</span>
                          ) : (
                            <span className="font-semibold text-text-primary">{creditInfo.credits}/mo</span>
                          )}
                        </div>
                        {!creditInfo.isUnlimited && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-text-secondary">Rollover Cap</span>
                            <span className="font-semibold text-text-primary">{creditInfo.rolloverCap}</span>
                          </div>
                        )}
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-text-secondary">Chat & Follow-ups</span>
                          <span className="font-semibold text-verdict-primary">Unlimited</span>
                        </div>
                        <div className="h-px bg-white/10" />
                        {/* Credit-to-query translation */}
                        {!creditInfo.isUnlimited && (
                          <div className="text-[11px] text-text-muted space-y-1 py-1">
                            <div className="flex justify-between">
                              <span>Financial analyses</span>
                              <span className="text-text-primary">{estimateQueries(parseInt(creditInfo.credits.replace(/,/g, ''))).financial}/mo</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Strategic blueprints</span>
                              <span className="text-text-primary">{estimateQueries(parseInt(creditInfo.credits.replace(/,/g, ''))).strategic}/mo</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <ul className="space-y-2 mb-6">
                      {tier.features.slice(0, 5).map((f) => (
                        <li key={f} className="flex items-start gap-2 text-xs text-text-secondary">
                          <Check className="w-3.5 h-3.5 text-verdict-primary flex-shrink-0 mt-0.5" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to="/signup" className="block">
                      <button className={cn(
                        'w-full py-3 rounded-lg text-sm font-medium transition-all duration-200',
                        isPopular ? 'bg-verdict-primary text-black hover:bg-verdict-primary/90'
                          : isTeam ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-black hover:from-amber-400 hover:to-orange-400'
                          : 'border border-border-subtle text-text-primary hover:bg-surface-light'
                      )}>
                        Subscribe Now <ArrowRight className="w-4 h-4 inline ml-2" />
                      </button>
                    </Link>
                  </SpotlightCard>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="relative py-16 sm:py-24 border-t border-border-subtle">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <SectionLabel centered className="mb-4">HOW IT WORKS</SectionLabel>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-4">
              Simple. <span className="text-verdict-primary">Predictable.</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-6 text-center">
            <div className="p-6 rounded-lg border border-border-subtle bg-white/[0.02]">
              <div className="w-12 h-12 rounded-full bg-verdict-primary/10 flex items-center justify-center mx-auto mb-4">
                <Coins className="w-6 h-6 text-verdict-primary" />
              </div>
              <h3 className="font-semibold mb-2 text-sm">Get Credits</h3>
              <p className="text-xs text-text-muted leading-relaxed">
                Subscribe for monthly credits or buy top-up packs anytime. Credits power all deep research queries.
              </p>
            </div>
            <div className="p-6 rounded-lg border border-border-subtle bg-white/[0.02]">
              <div className="w-12 h-12 rounded-full bg-verdict-primary/10 flex items-center justify-center mx-auto mb-4">
                <Search className="w-6 h-6 text-verdict-primary" />
              </div>
              <h3 className="font-semibold mb-2 text-sm">Research Anything</h3>
              <p className="text-xs text-text-muted leading-relaxed">
                Each query uses credits based on complexity. Multi-agent debate, fact-checking, portfolio scans, and verified verdicts included.
              </p>
            </div>
            <div className="p-6 rounded-lg border border-border-subtle bg-white/[0.02]">
              <div className="w-12 h-12 rounded-full bg-verdict-primary/10 flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="w-6 h-6 text-verdict-primary" />
              </div>
              <h3 className="font-semibold mb-2 text-sm">Chat Unlimited</h3>
              <p className="text-xs text-text-muted leading-relaxed">
                Follow-up questions and chat are always free. Dig deeper into any verdict without using credits.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="relative py-16 sm:py-24 border-t border-border-subtle">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <SectionLabel centered className="mb-4">FAQ</SectionLabel>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
              Common Questions
            </h2>
          </div>
          <div className="rounded-lg border border-border-subtle bg-white/[0.02] px-6">
            {FAQ_ITEMS.map((item) => (
              <FaqItem key={item.q} question={item.q} answer={item.a} />
            ))}
          </div>
          <div className="text-center mt-10">
            <p className="text-text-muted text-sm">
              Still have questions?{' '}
              <a href="mailto:contact@vettis.ai" className="text-verdict-primary hover:underline">
                contact@vettis.ai
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <VerdictFooter />
    </div>
  );
}
